/*
 * File: abbyCadabbyApi.ts
 * Project: rosita
 * -----
 * Last Modified: 03 Mar 2022 12:22:31 am
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';
import { ApiMultipleResponse } from 'models/ApiModels';
import { Application, ApplicationParams, CreateApplicationPayload, EditApplicationPayload } from 'models/Application';
import { ReduxState } from 'redux/store';

const baseUrl = process.env.REACT_APP_FREDDY_CADABBY_BASE_URL;
const functionsKey = process.env.REACT_APP_API_HOST_KEY_FREDDY_CADABBY;

export const freddyCadabbyApi = createApi({
  reducerPath: 'freddyCadabbyApi',
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as ReduxState).app.accessToken;

      if (token) {
        headers.set('authorization', `Bearer ${token}`);
        headers.set('x-functions-key', functionsKey);
        headers.set('Content-Type', 'application/json');
      }

      return headers;
    }
  }),
  tagTypes: ['Application', 'Applications'],
  endpoints: (builder) => ({
    getApplication: builder.query<Application, string>({
      query: (applicationId) => ({
        url: `/applications/${applicationId}`
      }),
      providesTags: ['Application'],
      keepUnusedDataFor: 300,
      extraOptions: {
        maxRetries: 3
      }
    }),
    updateApplication: builder.mutation<Application, { applicationId: string; payload: EditApplicationPayload }>({
      query: ({ applicationId, payload }) => ({
        url: `/applications/${applicationId}`,
        method: 'PUT',
        body: payload
      }),
      invalidatesTags: ['Application', 'Applications']
    }),
    createApplication: builder.mutation<Application, { payload: CreateApplicationPayload }>({
      query: ({ payload }) => ({
        url: '/applications',
        method: 'POST',
        body: payload
      }),
      invalidatesTags: ['Application']
    }),
    getApplications: builder.query<ApiMultipleResponse<Application>, ApplicationParams>({
      query: (params) => ({
        url: '/applications/',
        params
      }),
      providesTags: ['Applications'],
      keepUnusedDataFor: 300,
      extraOptions: {
        maxRetries: 3
      },
      transformResponse: (response: ApiMultipleResponse<Application>): ApiMultipleResponse<Application> => {
        return {
          ...response,
          data: response.data.sort((a, b) => a.name.localeCompare(b.name))
        };
      }
    }),
    getVersion: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/version',
        responseHandler: (response): Promise<string> => response.text()
      }),
      keepUnusedDataFor: 3600
    }),
    getApiName: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/apiName',
        responseHandler: (response): Promise<string> => response.text()
      }),
      keepUnusedDataFor: 3600
    })
  })
});

export const { useGetApplicationQuery, useGetVersionQuery, useGetApiNameQuery, useCreateApplicationMutation, useGetApplicationsQuery, useUpdateApplicationMutation, useLazyGetApplicationsQuery } =
  freddyCadabbyApi;
